/**
 * Oversikt → Oppfølging (manager redesign, 2026-10-09): one queue across dates, grouped by area.
 * Each row links into the page that owns its decision, carrying the person, the date and the
 * period; a claim or a swap keeps its two answers here, the same request writes Varsler used.
 * Nothing is approved twice: there is no second approval state.
 */
import { errorText } from '@/api/client';
import { useRequestWrites } from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { Button, Empty, Skeleton, toast } from '@/ui';
import { Icon } from '@/ui/icons';
import { Explain } from '@/ui/page';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { GROUPS, type Group, type Point, type Queue } from './queue';
import type { FollowUp } from './useQueue';

/** How many rows a group shows before "Vis N flere". */
const FIRST = 3;
const pts = (n: number) => `${n} ${n === 1 ? T('punkt') : T('punkter')}`;

export function OppfolgingTab({
  queue,
  loading,
  failed,
  unsure = [],
  onRetry,
}: { queue: Queue; loading: boolean; failed: boolean; unsure?: FollowUp['unsure']; onRetry?: () => void }) {
  const can = useCan();
  const rw = useRequestWrites();
  const [sp, setSp] = useSearchParams();
  const [open, setOpen] = useState<Set<Group>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const area = (GROUPS.some(([g]) => g === sp.get('omrade')) ? sp.get('omrade') : 'alle') as Group | 'alle';
  const pick = (g: Group | 'alle') => {
    const next = new URLSearchParams(sp);
    if (g === 'alle') next.delete('omrade');
    else next.set('omrade', g);
    setSp(next, { replace: true });
  };
  // a push links to one point, /varsler?valgt=abs12 (#133): its group opens in full, the row is
  // marked and scrolled to
  const picked = /^[\w-]+$/.test(sp.get('valgt') ?? '') ? sp.get('valgt') : null;
  const pickedGroup = queue.points.find((p) => p.key === picked)?.group;
  useEffect(() => {
    if (!loading && picked)
      document.querySelector(`[data-key="${picked}"]`)?.scrollIntoView?.({ block: 'center' });
  }, [loading, picked]);
  const n = queue.points.length;
  const present = GROUPS.filter(
    ([g]) => queue.points.some((p) => p.group === g) || unsure.some((s) => s.groups.includes(g)),
  );

  const decide = async (p: Point, yes: boolean) => {
    const dcs = p.decision;
    if (!dcs) return;
    setBusy(p.key);
    try {
      if (dcs.kind === 'offer') await (yes ? rw.approveOffer : rw.rejectOffer).mutateAsync({ id: dcs.id });
      else await (yes ? rw.approveSwap : rw.rejectSwap).mutateAsync({ id: dcs.id });
      toast(yes ? T(dcs.done) : T('Avslått'));
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const row = (p: Point) => (
    <li
      key={p.key}
      className="oxp"
      data-testid="queue-row"
      data-key={p.key}
      aria-current={p.key === picked ? 'true' : undefined}
    >
      <span className={`oxpi ${p.tone}`} aria-hidden="true" />
      <div className="oxpt">
        <small>{p.meta}</small>
        <b>{p.title}</b>
        <span>
          {p.sub}
          {p.records != null && (
            <>
              {' '}
              <span className="oxrec">({T('{n} i listen', { n: p.records })})</span>
            </>
          )}
        </span>
      </div>
      <div className="oxpa">
        {p.decision && can('vaktplan.endre') ? (
          <>
            <Link className="btn" to={p.to}>
              <Icon name="calendar" />
              {T('Se vaktene')}
            </Link>
            <Button
              disabled={busy === p.key}
              onClick={() => decide(p, false)}
              aria-label={`${T('Avslå')}: ${p.title}`}
            >
              {T('Avslå')}
            </Button>
            <Button
              tone="pri"
              disabled={busy === p.key}
              onClick={() => decide(p, true)}
              aria-label={`${T('Godkjenn')}: ${p.title}`}
            >
              <Icon name="check" />
              {T('Godkjenn')}
            </Button>
          </>
        ) : (
          <Link className="btn" to={p.to} aria-label={`${T(p.label)}: ${p.title}`}>
            {T(p.label)}
          </Link>
        )}
      </div>
    </li>
  );

  return (
    <div className="oxq">
      <div className="oxqh">
        <div>
          <h2>{T('Klart for deg')}</h2>
          <p>
            {loading || failed
              ? T('Oversikten er ikke komplett · alle team')
              : T('{n} å følge opp · alle team, på tvers av perioder', { n: pts(n) })}
          </p>
          <p className="oxmute">{T('Punktene er samlet etter område. Se dato og status på hvert punkt.')}</p>
        </div>
      </div>
      <Explain q={T('Hva teller som et punkt?')}>
        <p>
          {T(
            'Ett punkt er én rad du kan handle på. En arbeidsliste med mange registreringer er ett punkt; antallet står på raden. Totalen, overskriftene og tallet på fanen teller punkter.',
          )}
        </p>
        <p>
          {T(
            'Det som venter på en medarbeider, som en vakt som er tilbudt bort uten at noen har tatt den, står for seg nederst og telles ikke.',
          )}
        </p>
        <p>
          {T('Varslene under bjella er dine personlige meldinger. Å lese et varsel avgjør ingenting her.')}
        </p>
      </Explain>

      {(present.length > 1 || area !== 'alle') && (
        <fieldset className="mchips">
          <legend className="sr">{T('Område')}</legend>
          <button type="button" aria-pressed={area === 'alle'} onClick={() => pick('alle')}>
            {T('Alle')}
          </button>
          {GROUPS.filter(([g]) => present.some(([key]) => key === g) || area === g).map(([g, label]) => (
            <button key={g} type="button" aria-pressed={area === g} onClick={() => pick(g)}>
              {T(label)}
            </button>
          ))}
        </fieldset>
      )}

      {(failed || loading) && (
        <div className="mnote warn" aria-live="polite">
          <div className="mnt">
            <b>{failed ? T('Kunne ikke oppdatere hele oversikten') : T('Henter resten av oversikten…')}</b>
            <span>
              {unsure.length ? unsure.map((s) => T(s.label)).join(', ') : T('Noen opplysninger mangler')}.{' '}
              {T('Viste punkter kan være utdatert. Vi kan ikke bekrefte at alt er fulgt opp.')}
            </span>
          </div>
          {failed && <Button onClick={onRetry}>{T('Prøv igjen')}</Button>}
        </div>
      )}
      {area !== 'alle' &&
        n > 0 &&
        !queue.points.some((p) => p.group === area) &&
        !unsure.some((s) => s.groups.includes(area)) && (
          <section className="mcard">
            <Empty>
              {T('Ingen punkter i dette området.')}{' '}
              <button type="button" className="mlink" onClick={() => pick('alle')}>
                {T('Vis alle områder')}
              </button>
            </Empty>
          </section>
        )}
      {loading && n === 0 ? (
        <Skeleton rows={4} />
      ) : n === 0 && !failed ? (
        <section className="mcard">
          <Empty tone="done">{T('Ingenting å følge opp nå.')}</Empty>
        </section>
      ) : (
        GROUPS.filter(([g]) => area === 'alle' || area === g).map(([g, label]) => {
          const mine = queue.points.filter((p) => p.group === g);
          const gaps = unsure.filter((s) => s.groups.includes(g));
          if (!mine.length && !gaps.length) return null;
          const all = open.has(g) || area === g || pickedGroup === g;
          const shown = all ? mine : mine.slice(0, FIRST);
          return (
            <section
              className="mcard oxgroup"
              key={g}
              aria-labelledby={`ovg-${g}`}
              data-testid={`queue-${g}`}
            >
              <div className="mch">
                <h2 id={`ovg-${g}`}>{T(label)}</h2>
                <span className="sp" />
                <span className="sub">{gaps.length ? T('Ikke komplett') : pts(mine.length)}</span>
              </div>
              {gaps.length > 0 && (
                <p className="oxpad oxmute">
                  {T('Mangler oppdaterte opplysninger om')} {gaps.map((s) => T(s.label)).join(', ')}.{' '}
                  {mine.length
                    ? T('Viser tilgjengelige punkter.')
                    : T('Kan ikke bekrefte om det er punkter her.')}
                </p>
              )}
              <ul className="oxpl">{shown.map(row)}</ul>
              {mine.length > shown.length && (
                <div className="mcf">
                  <button
                    type="button"
                    className="mlink"
                    onClick={() => setOpen((s) => new Set(s).add(g))}
                    aria-expanded={false}
                  >
                    {T('Vis {n} til i {area}', {
                      n: mine.length - shown.length,
                      area: T(label).toLowerCase(),
                    })}
                  </button>
                </div>
              )}
            </section>
          );
        })
      )}

      {queue.waiting.length > 0 && (
        <details className="mexp oxwait" data-testid="queue-waiting">
          <summary>
            {T('Venter på medarbeider')} · {queue.waiting.length}
          </summary>
          <section className="mcard">
            <ul className="oxpl">{queue.waiting.map(row)}</ul>
          </section>
        </details>
      )}
    </div>
  );
}
